import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  Building, 
  MapPin, 
  Phone, 
  Mail, 
  Globe,
  Calendar,
  Users,
  Edit,
  Trash2,
  X,
  TrendingUp,
  Activity,
  AlertTriangle,
  CheckCircle,
  Clock,
  BarChart3,
  PieChart
} from 'lucide-react';
import { facilityAPI } from '../../services/facilityApi';
import { Facility, FacilityAssessmentStats, FacilityType, FacilityStatus } from '../../types/facility';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  max-width: 1000px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  padding: 24px 24px 0;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #111827;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  color: #6b7280;
  
  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 8px;
  border-bottom: 2px solid #e5e7eb;
`;

const InfoGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f9fafb;
  border-radius: 8px;
`;

const InfoIcon = styled.div<{ color: string }>`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
`;

const InfoContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const InfoLabel = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const InfoValue = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #111827;
`;

const StatusBadge = styled.span<{ status: FacilityStatus }>`
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => {
    const colors = {
      'Active': '#dcfce7',
      'Inactive': '#fee2e2',
      'Pending': '#fef3c7',
      'Suspended': '#f3f4f6'
    };
    return colors[props.status] || '#f3f4f6';
  }};
  color: ${props => {
    const colors = {
      'Active': '#166534',
      'Inactive': '#dc2626',
      'Pending': '#92400e',
      'Suspended': '#374151'
    };
    return colors[props.status] || '#374151';
  }};
`;

const TypeBadge = styled.span<{ type: FacilityType }>`
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => {
    const colors = {
      'Hospital': '#dbeafe',
      'Clinic': '#dcfce7',
      'Nursing Home': '#fef3c7',
      'Rehabilitation Center': '#e0e7ff',
      'Urgent Care': '#fce7f3',
      'Specialty Center': '#f3e8ff',
      'Post Acute': '#f0f9ff',
      'Healthcare Center': '#ecfdf5',
      'Care Center': '#fef7ed',
      'Wellness Center': '#fdf2f8',
      'Other': '#f3f4f6'
    };
    return colors[props.type] || '#f3f4f6';
  }};
  color: ${props => {
    const colors = {
      'Hospital': '#1e40af',
      'Clinic': '#166534',
      'Nursing Home': '#92400e',
      'Rehabilitation Center': '#3730a3',
      'Urgent Care': '#be185d',
      'Specialty Center': '#7c3aed',
      'Post Acute': '#0c4a6e',
      'Healthcare Center': '#14532d',
      'Care Center': '#9a3412',
      'Wellness Center': '#be185d',
      'Other': '#374151'
    };
    return colors[props.type] || '#374151';
  }};
`;

const SpecialtiesList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const SpecialtyTag = styled.span`
  padding: 4px 8px;
  background: #e0e7ff;
  color: #3730a3;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 16px;
  margin-top: 16px;
`;

const StatCard = styled.div`
  background: white;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 4px;
`;

const StatLabel = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ModalFooter = styled.div`
  padding: 0 24px 24px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 12px 24px;
  border-radius: 8px;
  border: none;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
  
  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
          color: white;
          &:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
          }
        `;
      case 'danger':
        return `
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          color: white;
          &:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
          }
        `;
      default:
        return `
          background: white;
          color: #374151;
          border: 1px solid #d1d5db;
          &:hover {
            background: #f9fafb;
            border-color: #9ca3af;
          }
        `;
    }
  }}
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #e5e7eb;
  border-top: 4px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  background: #fee2e2;
  color: #dc2626;
  padding: 16px;
  border-radius: 8px;
  margin: 20px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

interface FacilityDetailsProps {
  facilityId: string;
  onClose: () => void;
  onEdit: (facility: Facility) => void;
  onDelete: (facilityId: string) => void;
}

const FacilityDetails: React.FC<FacilityDetailsProps> = ({ 
  facilityId, 
  onClose, 
  onEdit, 
  onDelete 
}) => {
  const [facility, setFacility] = useState<Facility | null>(null);
  const [assessmentStats, setAssessmentStats] = useState<FacilityAssessmentStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadFacilityDetails();
  }, [facilityId]);

  const loadFacilityDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [facilityData, statsData] = await Promise.all([
        facilityAPI.getFacility(facilityId),
        facilityAPI.getFacilityAssessmentStats(facilityId).catch(() => null)
      ]);
      
      setFacility(facilityData);
      setAssessmentStats(statsData);
    } catch (err) {
      setError('Failed to load facility details');
      console.error('Error loading facility details:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this facility?')) {
      onDelete(facilityId);
      onClose();
    }
  };

  if (loading) {
    return (
      <ModalOverlay onClick={onClose}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <LoadingContainer>
            <LoadingSpinner />
          </LoadingContainer>
        </ModalContent>
      </ModalOverlay>
    );
  }

  if (error || !facility) {
    return (
      <ModalOverlay onClick={onClose}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>Error</ModalTitle>
            <CloseButton onClick={onClose}>
              <X size={24} />
            </CloseButton>
          </ModalHeader>
          <ModalBody>
            <ErrorMessage>
              <AlertTriangle size={20} />
              {error || 'Facility not found'}
            </ErrorMessage>
          </ModalBody>
        </ModalContent>
      </ModalOverlay>
    );
  }

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            <Building size={24} />
            {facility.name}
          </ModalTitle>
          <CloseButton onClick={onClose}>
            <X size={24} />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <ContentGrid>
            <Section>
              <SectionTitle>
                <Building size={20} />
                Basic Information
              </SectionTitle>
              
              <InfoGrid>
                <InfoItem>
                  <InfoIcon color="#3b82f6">
                    <Building size={16} />
                  </InfoIcon>
                  <InfoContent>
                    <InfoLabel>Facility Name</InfoLabel>
                    <InfoValue>{facility.name}</InfoValue>
                  </InfoContent>
                </InfoItem>

                <InfoItem>
                  <InfoIcon color="#10b981">
                    <Activity size={16} />
                  </InfoIcon>
                  <InfoContent>
                    <InfoLabel>Type & Status</InfoLabel>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <TypeBadge type={facility.type}>{facility.type}</TypeBadge>
                      <StatusBadge status={facility.status}>{facility.status}</StatusBadge>
                    </div>
                  </InfoContent>
                </InfoItem>

                {facility.capacity && (
                  <InfoItem>
                    <InfoIcon color="#f59e0b">
                      <Users size={16} />
                    </InfoIcon>
                    <InfoContent>
                      <InfoLabel>Capacity</InfoLabel>
                      <InfoValue>{facility.capacity} beds</InfoValue>
                    </InfoContent>
                  </InfoItem>
                )}

                <InfoItem>
                  <InfoIcon color="#8b5cf6">
                    <Calendar size={16} />
                  </InfoIcon>
                  <InfoContent>
                    <InfoLabel>Established</InfoLabel>
                    <InfoValue>{new Date(facility.establishedDate).toLocaleDateString()}</InfoValue>
                  </InfoContent>
                </InfoItem>
              </InfoGrid>

              {facility.specialties.length > 0 && (
                <>
                  <SectionTitle>
                    <Activity size={20} />
                    Specialties
                  </SectionTitle>
                  <SpecialtiesList>
                    {facility.specialties.map((specialty) => (
                      <SpecialtyTag key={specialty}>{specialty}</SpecialtyTag>
                    ))}
                  </SpecialtiesList>
                </>
              )}

              {facility.description && (
                <>
                  <SectionTitle>
                    <Building size={20} />
                    Description
                  </SectionTitle>
                  <div style={{ 
                    padding: '16px', 
                    background: '#f9fafb', 
                    borderRadius: '8px',
                    fontSize: '14px',
                    lineHeight: '1.6',
                    color: '#374151'
                  }}>
                    {facility.description}
                  </div>
                </>
              )}
            </Section>

            <Section>
              <SectionTitle>
                <MapPin size={20} />
                Contact Information
              </SectionTitle>
              
              <InfoGrid>
                <InfoItem>
                  <InfoIcon color="#ef4444">
                    <MapPin size={16} />
                  </InfoIcon>
                  <InfoContent>
                    <InfoLabel>Address</InfoLabel>
                    <InfoValue>
                      {facility.address.street}<br />
                      {facility.address.city}, {facility.address.state} {facility.address.zipCode}<br />
                      {facility.address.country}
                    </InfoValue>
                  </InfoContent>
                </InfoItem>

                <InfoItem>
                  <InfoIcon color="#10b981">
                    <Phone size={16} />
                  </InfoIcon>
                  <InfoContent>
                    <InfoLabel>Phone</InfoLabel>
                    <InfoValue>{facility.contact.phone}</InfoValue>
                  </InfoContent>
                </InfoItem>

                <InfoItem>
                  <InfoIcon color="#3b82f6">
                    <Mail size={16} />
                  </InfoIcon>
                  <InfoContent>
                    <InfoLabel>Email</InfoLabel>
                    <InfoValue>{facility.contact.email}</InfoValue>
                  </InfoContent>
                </InfoItem>

                {facility.contact.website && (
                  <InfoItem>
                    <InfoIcon color="#8b5cf6">
                      <Globe size={16} />
                    </InfoIcon>
                    <InfoContent>
                      <InfoLabel>Website</InfoLabel>
                      <InfoValue>
                        <a 
                          href={facility.contact.website} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ color: '#3b82f6', textDecoration: 'none' }}
                        >
                          {facility.contact.website}
                        </a>
                      </InfoValue>
                    </InfoContent>
                  </InfoItem>
                )}
              </InfoGrid>

              {assessmentStats && (
                <>
                  <SectionTitle>
                    <BarChart3 size={20} />
                    Assessment Statistics
                  </SectionTitle>
                  
                  <StatsGrid>
                    <StatCard>
                      <StatValue>{assessmentStats.totalAssessments}</StatValue>
                      <StatLabel>Total Assessments</StatLabel>
                    </StatCard>
                    
                    <StatCard>
                      <StatValue>{assessmentStats.averageScore.toFixed(1)}</StatValue>
                      <StatLabel>Average Score</StatLabel>
                    </StatCard>
                    
                    <StatCard>
                      <StatValue>{assessmentStats.scoreDistribution.lowRisk}</StatValue>
                      <StatLabel>Low Risk</StatLabel>
                    </StatCard>
                    
                    <StatCard>
                      <StatValue>{assessmentStats.scoreDistribution.moderateRisk}</StatValue>
                      <StatLabel>Moderate Risk</StatLabel>
                    </StatCard>
                    
                    <StatCard>
                      <StatValue>{assessmentStats.scoreDistribution.highRisk}</StatValue>
                      <StatLabel>High Risk</StatLabel>
                    </StatCard>
                  </StatsGrid>
                </>
              )}
            </Section>
          </ContentGrid>
        </ModalBody>

        <ModalFooter>
          <Button type="button" onClick={onClose}>
            Close
          </Button>
          <Button 
            type="button" 
            variant="primary" 
            onClick={() => onEdit(facility)}
          >
            <Edit size={20} />
            Edit Facility
          </Button>
          <Button 
            type="button" 
            variant="danger" 
            onClick={handleDelete}
          >
            <Trash2 size={20} />
            Delete Facility
          </Button>
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
};

export default FacilityDetails;
